import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './OrganizationsSearch.module.scss';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import { useHasPrivilege } from '../../components/authorization';
import { PRIVILEGES } from '../../api/types/dtos';

import SearchBar from '../../components/SearchBar';
import LoadingSpinner from '../../components/LoadingSpinner';
import Pagination from '../../components/Pagination';
import { useSearchOrganizations } from '../../api/hooks/organization.hooks';
import { useOrganization } from '../../contexts/OrganizationContext';
import OrganizationCard from './components/OrganizationCard';
import Filters from './components/Filters';
import { OrganizationSearchParams } from '../../api/types/dtos';
import { useOrganizationSelection } from '../../api/hooks/useOrganizationSelection';
import { useFetchAllOrgIds } from '../../hooks/useFetchAllOrgIds';
import { organizationService } from '../../api/services/organization.service';
import ReportRequestDialog from '../../components/reports/ReportRequestDialog';
import FarmDetailsModal from '../../components/modal/FarmDetailsModal';
import EditFarmDetailsModal from '../../components/modal/EditFarmDetailsModal';
import { GroupSelectionModal } from '../../components/modal/GroupSelectionModal';
import { usePreloadDynamicPropertyTypes } from '../../api/hooks/useDynamicPropertyTypes';
import { useOrganizationDynamicProperties } from '../../api/hooks';

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'createdAt', label: 'Creation Date' },
];

const ITEMS_PER_PAGE_OPTIONS = [12, 24, 48, 96] as const;
const DEFAULT_ITEMS_PER_PAGE = 12;

export const OrganizationsSearch: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [itemsPerPage, setItemsPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const { selectedOrganization, loading: orgLoading, error: orgError } = useOrganization();

  // Preload dynamic property types when entering this route
  const { data: dynamicPropertyTypes, isLoading: dynamicPropertyTypesLoading } = usePreloadDynamicPropertyTypes();

  // Get organization dynamic properties for the current organization
  const { data: organizationDynamicProperties } = useOrganizationDynamicProperties(
    selectedOrganization?.id || ''
  );

  // Organization selection state
  const {
    selectedIds,
    toggleSelection,
    selectMultiple,
    unselectMultiple,
    clearSelection,
    isSelected,
    selectedCount
  } = useOrganizationSelection();

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Authorization check
  const { hasPrivilege, loading: authLoading, error: authError } = useHasPrivilege('READ_ORGANIZATION');
  const { hasPrivilege: canEditOrg } = useHasPrivilege('UPDATE_ORGANIZATION');
  const { hasPrivilege: canCreateReport } = useHasPrivilege(PRIVILEGES.CREATE_REPORT);

  // Report-from-anywhere state (reuses the shared org selection)
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Get all search parameters
  const searchTerm = searchParams.get('searchTerm') || '';
  const farmerLocationCity = searchParams.get('farmerLocationCity') || '';
  const farmerLocationState = searchParams.get('farmerLocationState') || '';
  const farmerLocationCountry = searchParams.get('farmerLocationCountry') || '';

  // Fetch every org matching the current filters (for "select all filter" reports)
  const { fetchAll: fetchAllOrgIds, isLoading: selectingAllForReport, progress: selectAllProgress } =
    useFetchAllOrgIds(
      useCallback(
        async (page: number, size: number) => {
          const res = await organizationService.searchChildOrganizations({
            searchTerm,
            parentOrganizationId: selectedOrganization?.id || '',
            farmerLocationCity,
            farmerLocationState,
            farmerLocationCountry,
            sortBy,
            page,
            size,
          });
          const content = res.data?.content ?? [];
          return {
            items: content.map((org) => ({ id: org.id ?? '', name: org.name ?? '' })),
            totalElements: res.data?.totalElements ?? 0,
            totalPages: res.data?.totalPages ?? 0,
          };
        },
        [searchTerm, selectedOrganization?.id, farmerLocationCity, farmerLocationState, farmerLocationCountry, sortBy]
      )
    );



  // Get dynamic title from organization properties or fallback to default
  const getDynamicTitle = (): string => {
    // First, try to find the property in organization dynamic properties
    if (organizationDynamicProperties?.data) {
      const titleProperty = organizationDynamicProperties.data.find(
        prop => prop.propertyTypeName === 'main_section_title'
      );
      
      if (titleProperty?.value) {
        return titleProperty.value;
      }
    }
    
    // Fallback: get default value from dynamic property types cache
    if (dynamicPropertyTypes?.data) {
      const titleType = dynamicPropertyTypes.data.find(
        type => type.name === 'main_section_title'
      );
      
      if (titleType?.defaultValue) {
        return titleType.defaultValue;
      }
    }
    
    // Final fallback: hardcoded title
    return 'Administra tus productores';
  };

  // Get dynamic search title from organization properties or fallback to default
  const getDynamicSearchTitle = (): string => {
    // First, try to find the property in organization dynamic properties
    if (organizationDynamicProperties?.data) {
      const searchTitleProperty = organizationDynamicProperties.data.find(
        prop => prop.propertyTypeName === 'main_section_search_title'
      );
      
      if (searchTitleProperty?.value) {
        return searchTitleProperty.value;
      }
    }
    
    // Fallback: get default value from dynamic property types cache
    if (dynamicPropertyTypes?.data) {
      const searchTitleType = dynamicPropertyTypes.data.find(
        type => type.name === 'main_section_search_title'
      );
      
      if (searchTitleType?.defaultValue) {
        return searchTitleType.defaultValue;
      }
    }
    
    // Final fallback: hardcoded search title
    return 'Buscar una Finca';
  };

  // Get dynamic search text (placeholder) from organization properties or fallback to default
  const getDynamicSearchText = (): string => {
    // First, try to find the property in organization dynamic properties
    if (organizationDynamicProperties?.data) {
      const searchTextProperty = organizationDynamicProperties.data.find(
        prop => prop.propertyTypeName === 'main_section_search_text'
      );
      
      if (searchTextProperty?.value) {
        return searchTextProperty.value;
      }
    }
    
    // Fallback: get default value from dynamic property types cache
    if (dynamicPropertyTypes?.data) {
      const searchTextType = dynamicPropertyTypes.data.find(
        type => type.name === 'main_section_search_text'
      );
      
      if (searchTextType?.defaultValue) {
        return searchTextType.defaultValue;
      }
    }
    
    // Final fallback: hardcoded search text
    return 'Finca...';
  };



  // Reset to first page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, farmerLocationCity, farmerLocationState, farmerLocationCountry]);

  // Reset to first page when items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Query parameters
  const queryParams: OrganizationSearchParams = {
    searchTerm,
    sortBy,
    size: itemsPerPage,
    page: currentPage - 1,
    parentOrganizationId: selectedOrganization?.id || '',
    farmerLocationCity,
    farmerLocationState,
    farmerLocationCountry,
  };

  // Fetch organizations
  const { data, isLoading, error } = useSearchOrganizations(queryParams, {
    enabled: !orgLoading && !!selectedOrganization?.id,
    queryKey: ['organizations', 'search', queryParams],
    retry: 1,
    staleTime: 30000,
    refetchOnWindowFocus: false
  });

  // Handlers
  const handleSearch = (term: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (term) {
      newParams.set('searchTerm', term);
    } else {
      newParams.delete('searchTerm');
    }
    setSearchParams(newParams);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFiltersChange = (filters: Partial<OrganizationSearchParams>) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
    });
    
    setSearchParams(newParams);
  };

  const handleSelectAll = (checked: boolean) => {
    if (!data?.data?.content) return;
    
    const currentPageIds = data.data.content
      .map(org => org.id)
      .filter((id): id is string => id != null);

    if (checked) {
      selectMultiple(currentPageIds);
    } else {
      unselectMultiple(currentPageIds);
    }
  };

  const handleOrganizationClick = (organizationId: string) => {
    setSelectedOrganizationId(organizationId);
  };

  const handleEditOrganization = (organization: any) => {
    setEditingOrganization(organization);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingOrganization(null);
  };

  const handleCloseModal = () => {
    setSelectedOrganizationId(null);
  };

  const handleAddToGroup = () => {
    if (selectedCount === 0) {
      enqueueSnackbar('Please select at least one organization', { variant: 'warning' });
      return;
    }
    setIsGroupModalOpen(true);
  };

  const handleGroupModalClose = () => {
    setIsGroupModalOpen(false);
  };

  // Reports from anywhere: select all orgs matching current filters
  const handleSelectAllForReport = async () => {
    const orgs = await fetchAllOrgIds();
    if (orgs.length > 0) {
      selectMultiple(orgs.map((org) => org.id));
    }
  };

  // Selected orgs with names, for the report dialog
  const selectedReportOrgs = useMemo(() => {
    const nameById = new Map(
      (data?.data?.content ?? []).map((org) => [org.id ?? '', org.name ?? ''])
    );
    return Array.from(selectedIds).map((id) => ({ id, name: nameById.get(id) ?? id }));
  }, [selectedIds, data?.data?.content]);

  // Computed values
  const areAllSelected = data?.data?.content?.every(org => org.id && isSelected(org.id)) || false;
  const hasPartialSelection = !areAllSelected && data?.data?.content?.some(org => org.id && isSelected(org.id));

    // Error handling
  // Authorization check - must be done before any content rendering
  if (authLoading) {
    return (
      <Container>
        <LoadingSpinner />
      </Container>
    );
  }

  // Handle authorization errors
  if (authError) {
    return (
      <Container>
        <Alert severity="error" className={styles.error}>
          <strong>Error de autorización:</strong> {typeof authError === 'string' ? authError : (authError as any)?.message || String(authError)}
          <br />
          <small>
            Si el problema persiste, contacta al administrador del sistema.
          </small>
        </Alert>
      </Container>
    );
  }

  if (!hasPrivilege) {
    return (
      <Container>
        <Alert severity="warning" className={styles.error}>
          No tienes permisos para ver las organizaciones. Contacta al administrador para solicitar acceso.
        </Alert>
      </Container>
    );
  }

  if (orgError) {
    return (
      <Container>
        <Alert severity="error" className={styles.error}>
          Error loading organization data: {orgError as any instanceof Error ? orgError.message : String(orgError)}
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" className={styles.error}>
          Error loading organizations: {error as any instanceof Error ? error.message : String(error)}
        </Alert>
      </Container>
    );
  }

  // Render content based on loading state
  const renderContent = () => {
    if (isLoading || orgLoading) {
      return <LoadingSpinner />;
    }

    if (!data?.data?.content?.length) {
      return (
        <div className={styles.emptyState}>
          <h2 className={styles.emptyStateText}>
            No organizations found
          </h2>
        </div>
      );
    }

    return (
      <>
        <div className={styles.selectionBar}>
          <FormControlLabel
            control={
              <Checkbox
                checked={areAllSelected}
                indeterminate={hasPartialSelection}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className={styles.selectionCheckbox}
              />
            }
            label={
              <span className={styles.selectionLabel}>
                {selectedCount > 0 
                  ? `Selected ${selectedCount} organization${selectedCount === 1 ? '' : 's'}`
                  : 'Seleccionar todo'}
              </span>
            }
          />
          {canCreateReport && (
            <Button
              variant="text"
              size="small"
              onClick={handleSelectAllForReport}
              disabled={selectingAllForReport}
            >
              {selectingAllForReport
                ? `${selectAllProgress?.loaded ?? 0}/${selectAllProgress?.total || '?'}`
                : 'Seleccionar todo el filtro'}
            </Button>
          )}
          {selectedCount > 0 && canEditOrg && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAddToGroup}
              className={styles.addToGroupButton}
            >
              Add to Group
            </Button>
          )}
          {canCreateReport && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setReportDialogOpen(true)}
              disabled={selectedCount === 0}
            >
              Descargar Reporte
            </Button>
          )}
        </div>

        <Grid container spacing={3} className={styles.grid}>
          {data.data.content.map((org) => (
            <Grid item xs={12} sm={6} md={3} key={org.id || Math.random()} className={styles.gridItem}>
              <OrganizationCard 
                organization={org}
                selected={!!org.id && isSelected(org.id)}
                onSelect={toggleSelection}
                onClick={() => org.id && handleOrganizationClick(org.id)}
                onEdit={canEditOrg ? handleEditOrganization : undefined}
              />
            </Grid>
          ))}
        </Grid>
        
        {data.data.totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={data.data.totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </>
    );
  };

  return (
    <Container maxWidth="xl">
      <div className={styles.container}>
        <h1 className={styles.title}>
          {getDynamicTitle()}
        </h1>

        <div className={styles.content}>
          {/* Filters Section */}
          <div className={styles.filters}>
            <Filters
              onChange={handleFiltersChange}
              initialValues={{
                farmerLocationCity,
                farmerLocationState,
                farmerLocationCountry,
              }}
            />
          </div>

          {/* Main Content Section */}
          <div className={styles.mainContent}>
            <div className={styles.searchControls}>
              <p className={styles.searchControlsText}>{getDynamicSearchTitle()}</p>
              <div className={styles.searchBar}>
                <SearchBar
                  onSearch={handleSearch}
                  initialValue={searchTerm}
                  placeholder={getDynamicSearchText()}
                />
              </div>
            </div>

            {renderContent()}
          </div>
        </div>
      </div>

      {selectedOrganizationId && (
        <FarmDetailsModal
          open={!!selectedOrganizationId}
          onClose={handleCloseModal}
          organizationId={selectedOrganizationId}
        />
      )}

      <EditFarmDetailsModal
        open={showEditModal}
        onClose={handleCloseEditModal}
        organization={editingOrganization}
      />

      <GroupSelectionModal
        open={isGroupModalOpen}
        onClose={handleGroupModalClose}
        selectedOrganizationIds={Array.from(selectedIds)}
      />

      {canCreateReport && (
        <ReportRequestDialog
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          orgs={selectedReportOrgs}
          sourceLabel="Búsqueda de organizaciones"
        />
      )}
    </Container>
  );
};

export default OrganizationsSearch; 
